import { useState, useEffect, useCallback, useMemo, useRef, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Award, Plus, Trash2, X, Check, ShieldCheck, Loader2, Sparkles, Store, WifiOff, AlertTriangle, RefreshCw, Eye, Users, Copy } from 'lucide-react';
import { apiService, LuckPermsGroupDTO, GroupNodeDTO, type ProjectGroupDTO, type RequestOptions } from '@/services/api.service';
import { getGameById, supportsGameGroups } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { useAllowedGameGroups, useGameGroupsStore, useUsableGameGroups } from '@/stores/gameGroups.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { gameGroupHeaderFor, onGameGroup, type GameGroupLabel } from '@/utils/gameGroups';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { GradeSettingsPanel, useGradeSettingsPlace, type GradeSettingsTab } from '@/components/config/GradeCosmetics';
import {
  adminOnlyNodeReason,
  createGradeOn,
  createSummary,
  isProtectedGrade,
  planGradeFrames,
  sortGradesByWeight,
  weightFromNodes,
  type CreateOutcome,
  type GradeFrame,
} from './gradesByGameGroup';
import { GradeMembers } from './GradeMembers';
import { GradeCopyDialog } from './GradeCopyDialog';
import styles from './GradesPage.module.scss';

/**
 * Grades in-game (Supervision) : les grades LuckPerms du jeu, leurs permissions et leurs héritages.
 *
 * Jeu à groupes (NationsGlory : Nova, Iris...) : chaque groupe a son LuckPerms, derrière son
 * satellite. La page montre un cadre par groupe de l'affectation du membre du staff (libellés et
 * couleurs du catalogue des groupes), côte à côte sur grand écran, empilés sur mobile. Chaque cadre
 * travaille sur SON groupe (requêtes à groupe imposé). Un seul cadre : celui du groupe choisi dans le
 * sélecteur du bandeau. Jeu sans groupes (Paladium, Recube) : un seul cadre, sans
 * en-tête de groupe, mêmes requêtes qu'avant.
 *
 * Le détail d'un grade porte aussi ses cosmétiques (ornement, rank tag, rendu du chat) et sa
 * boutique : choisis ici parmi le catalogue créé dans Configuration > Cosmétiques des grades, ils
 * s'écrivent dans tous les containers du projet, communs à tous les groupes de jeu.
 *
 * `?grade=modo` présélectionne un grade, `?tab=cosmetiques` (ou `boutique`) ouvre cet onglet du
 * détail : c'est la cible de l'ancienne adresse /config/cosmetics?grade=modo. `?tab=joueurs` ouvre
 * l'onglet Joueurs : qui porte le grade, et le donner ou le retirer (cf GradeMembers).
 */
export function GradesPage() {
  const { hasPage, canWrite } = useAuthStore();
  const canSee = hasPage('grades');
  const canEdit = canWrite('grades');
  // Cosmétiques et boutique d'un grade : même garde qu'avant leur arrivée ici, quand ils étaient
  // l'onglet Grades de Configuration > Cosmétiques des grades. Droit config.grades (ancien droit de
  // ces réglages) ET module « cosmetics » actif pour le projet (cf useGradeSettingsPlace). Le droit
  // d'écrire dans LuckPerms (grades) n'y entre pas : un grade qui n'a que config.grades voit les
  // permissions en lecture et règle les cosmétiques, comme avant.
  const canCosmetics = useGradeSettingsPlace() === 'supervision';
  // Onglet Joueurs : la liste des membres et la recherche d'un joueur sont des lectures de joueurs
  // (players.view à la passerelle) ; les écritures sont celles de la fiche joueur (players.manage).
  const canMembers = hasPage('players');
  const [searchParams] = useSearchParams();
  const initialGrade = searchParams.get('grade')?.toLowerCase() ?? null;
  const initialTab = detailTabOf(searchParams.get('tab'), canCosmetics, canMembers);

  const gameId = useGameStore((s) => s.activeGameId);
  const known = useGameStore((s) => !!s.gameGroupsKnown[s.activeGameId]);
  const persisted = useGameStore((s) => s.activeGameGroupByGame[s.activeGameId] ?? null);
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  const status = useGameGroupsStore((s) => s.status);
  const allowed = useAllowedGameGroups(gameId);
  // Groupes où « Créer aussi sur » peut écrire : l'affectation, acceptés par la passerelle.
  const usable = useUsableGameGroups(gameId);
  const projectName = getGameById(gameId).name;

  const active = useGameStore((s) => gameGroupHeaderFor(s.activeGameId, s.gameGroupsKnown, s.activeGameGroupByGame));
  const plan = useMemo(() => planGradeFrames({
    supportsGroups: supportsGameGroups(gameId), entry, status, known, persisted, allowed, projectName, active,
  }), [gameId, entry, status, known, persisted, allowed, projectName, active]);

  // Relecture d'un cadre quand un autre y a créé un grade (« Créer aussi sur »).
  const [reloadTicks, setReloadTicks] = useState<Record<string, number>>({});
  const bumpFrames = useCallback((keys: string[]) => {
    setReloadTicks((cur) => {
      const next = { ...cur };
      for (const k of keys) next[k] = (next[k] ?? 0) + 1;
      return next;
    });
  }, []);

  // La nav masque déjà l'entrée, mais l'URL reste atteignable à la main : la passerelle refuse de
  // toute façon, autant ne pas afficher des cadres vides.
  if (!canSee) {
    return (
      <div className={styles.page}>
        <div className={styles.detailEmpty}>
          <Award size={48} strokeWidth={1.5} />
          <p>Tu n'as pas accès aux grades in-game de {projectName}.</p>
        </div>
      </div>
    );
  }

  const grouped = plan.kind === 'frames' && plan.frames.some((f) => f.group !== null);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Grades</h1>
        <p className={styles.subtitle}>
          {grouped
            ? 'Gérez les grades LuckPerms du groupe choisi en haut, leurs permissions et leurs héritages.'
            : 'Gérez les grades LuckPerms, leurs permissions et leurs héritages.'}
          {canCosmetics && ' Ornement, rank tag, chat et boutique de chaque grade se règlent dans son détail, pour tous les containers du projet.'}
          {!canEdit && ' Permissions en lecture seule : ton grade ne permet pas de les modifier.'}
        </p>
      </header>

      {plan.kind === 'loading' ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement des groupes de jeu…</span></div>
      ) : plan.kind === 'empty' ? (
        <div className={styles.detailEmpty}>
          <Award size={48} strokeWidth={1.5} />
          <p>{plan.message}</p>
        </div>
      ) : (
        <>
          {plan.notice && (
            <div className={styles.protectedNotice}><AlertTriangle size={16} /> {plan.notice}</div>
          )}
          <div className={styles.frames}>
            {plan.frames.map((frame) => (
              <GradeFrameCard
                key={frame.group?.key ?? '-'}
                frame={frame}
                otherGroups={frame.group ? usable.filter((g) => g.key !== frame.group!.key) : []}
                canEdit={canEdit}
                canCosmetics={canCosmetics}
                canMembers={canMembers}
                initialGrade={initialGrade}
                initialTab={initialTab}
                reloadTick={frame.group ? reloadTicks[frame.group.key] ?? 0 : 0}
                onCreatedOn={bumpFrames}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Onglet du détail d'un grade. Joueurs, Cosmétiques et Boutique n'existent qu'avec leur droit. */
type DetailTab = 'permissions' | 'joueurs' | GradeSettingsTab;

function detailTabOf(raw: string | null, canCosmetics: boolean, canMembers: boolean): DetailTab {
  if (canMembers && raw === 'joueurs') return raw;
  return canCosmetics && (raw === 'cosmetiques' || raw === 'boutique') ? raw : 'permissions';
}

interface GradeFrameCardProps {
  frame: GradeFrame;
  /** Autres groupes utilisables, proposés par « Créer aussi sur » (vide sans groupes). */
  otherGroups: ProjectGroupDTO[];
  /** Droit d'écriture sur la page (créer, supprimer, éditer les nodes). */
  canEdit: boolean;
  /** Accès aux cosmétiques et à la boutique des grades (onglets du détail). */
  canCosmetics: boolean;
  /** Onglet Joueurs du détail (page Joueurs). */
  canMembers: boolean;
  /** Grade présélectionné (`?grade=`, minuscules) s'il existe dans ce cadre. */
  initialGrade: string | null;
  /** Onglet du détail à l'ouverture (`?tab=`). */
  initialTab: DetailTab;
  /** Change quand un autre cadre a créé un grade dans ce groupe : relecture de la liste. */
  reloadTick: number;
  onCreatedOn: (groupKeys: string[]) => void;
}

/**
 * Cadre d'un groupe de jeu (ou du jeu sans groupes) : liste des grades, création, suppression et
 * détail du grade sélectionné. Toutes ses requêtes visent son groupe.
 */
function GradeFrameCard({
  frame, otherGroups, canEdit, canCosmetics, canMembers, initialGrade, initialTab, reloadTick, onCreatedOn,
}: GradeFrameCardProps) {
  const group = frame.group;
  const groupKey = group?.key ?? null;
  const unavailable = frame.unavailable;
  // Groupe imposé à chaque requête du cadre ; jeu sans groupes : requêtes d'avant (aucun en-tête).
  const opts = useMemo<RequestOptions | undefined>(() => (groupKey ? { gameGroup: groupKey } : undefined), [groupKey]);

  const [grades, setGrades] = useState<LuckPermsGroupDTO[]>([]);
  const [protectedGrades, setProtectedGrades] = useState<string[]>([]);
  const [loading, setLoading] = useState(!unavailable);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // Onglet du détail, gardé d'un grade à l'autre : régler les cosmétiques de plusieurs grades à la
  // suite ne ramène pas sur Permissions à chaque clic.
  const [detailTab, setDetailTab] = useState<DetailTab>(initialTab);
  // `?grade=` n'est appliqué qu'à la première lecture réussie : une relecture (création,
  // suppression) ne doit pas ramener sur ce grade.
  const preselected = useRef(false);

  // Création de grade
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  // « Créer aussi sur … » : autres groupes cochés, jamais par défaut.
  const [alsoOn, setAlsoOn] = useState<string[]>([]);
  const extraGroups = otherGroups.filter((g) => alsoOn.includes(g.key));
  // « Copier les grades vers… » : fenêtre de copie de ce groupe vers un autre (cf GradeCopyDialog).
  const [copying, setCopying] = useState(false);

  const loadGrades = useCallback(async () => {
    if (unavailable) return;
    const res = await apiService.getAllGroups(opts);
    if (res.data) {
      let list = res.data.groups;
      // Satellite d'avant BE-Panel-Games 1.0.9 : la liste n'a pas les poids, lus dans les nodes de
      // chaque grade. Un grade illisible compte sans poids.
      if (list.length > 0 && list.every((g) => g.weight === undefined)) {
        list = await Promise.all(list.map(async (g) => {
          const p = await apiService.getGroupPermissions(g.name, opts);
          return { ...g, weight: p.data ? weightFromNodes(p.data.permissions) : null };
        }));
      }
      // Du plus lourd au plus léger, comme LuckPerms (cf sortGradesByWeight).
      const sorted = sortGradesByWeight(list);
      setGrades(sorted);
      setProtectedGrades((res.data.protectedGrades ?? []).map((n) => n.toLowerCase()));
      setLoadError(null);
      // `?grade=` : présélection à la première lecture, seulement si le grade existe dans ce groupe.
      if (initialGrade && !preselected.current) {
        preselected.current = true;
        setSelected((cur) => cur ?? sorted.find((g) => g.name.toLowerCase() === initialGrade)?.name ?? null);
      }
    } else {
      setLoadError(res.error ?? 'Lecture des grades impossible.');
    }
    setLoading(false);
  }, [opts, unavailable, initialGrade]);

  useEffect(() => {
    void loadGrades();
  }, [loadGrades, reloadTick]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || createBusy) return;
    setCreateError(null);
    setCreateBusy(true);
    const res = await apiService.createGroup(name, opts);
    if (res.error) {
      setCreateBusy(false);
      setCreateError(res.error);
      return;
    }
    // Autres groupes cochés : une requête chacun, dans l'ordre, un résultat par groupe. Sans groupe
    // coché (et toujours pour un jeu sans groupes), le déroulé est celui d'avant.
    if (group && extraGroups.length > 0) {
      const outcomes: CreateOutcome[] = [{ label: group.label, ok: true }];
      const createdOn: string[] = [];
      for (const g of extraGroups) {
        const r = await createGradeOn(name, g.key);
        outcomes.push({ label: g.label, ok: !r.error, error: r.error });
        if (!r.error) createdOn.push(g.key);
      }
      const summary = createSummary(name, outcomes);
      const notify = useNotificationsStore.getState();
      if (summary.ok) notify.success(summary.message); else notify.warning(summary.message, 12000);
      if (createdOn.length > 0) onCreatedOn(createdOn);
    }
    setCreateBusy(false);
    setNewName('');
    setAlsoOn([]);
    setCreating(false);
    await loadGrades();
    setSelected(name.toLowerCase().replace(/\s+/g, '_'));
  };

  // Poids relu dans le détail (node weight.N ajouté ou retiré) : la liste se retrie sans relecture.
  const updateWeight = useCallback((name: string, weight: number | null) => {
    setGrades((cur) => {
      const g = cur.find((x) => x.name === name);
      if (!g || (g.weight ?? null) === weight) return cur;
      return sortGradesByWeight(cur.map((x) => (x.name === name ? { ...x, weight } : x)));
    });
  }, []);

  const toggleAlsoOn = (key: string) =>
    setAlsoOn((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const handleDelete = async (name: string) => {
    if (!window.confirm(`Supprimer définitivement le grade "${name}"${onGameGroup(group)} ?`)) return;
    const res = await apiService.deleteGroup(name, opts);
    if (res.error) {
      useNotificationsStore.getState().error(`Suppression du grade « ${name} »${onGameGroup(group)} impossible : ${res.error}`);
      return;
    }
    if (selected === name) setSelected(null);
    await loadGrades();
  };

  const retry = () => {
    setLoading(true);
    void loadGrades();
  };

  let body;
  if (unavailable) {
    body = (
      <div className={styles.frameNotice}>
        <AlertTriangle size={16} /> <span>{group?.label ?? 'Ce groupe'} indisponible : {unavailable.replace(/[.\s]+$/, '')}.</span>
      </div>
    );
  } else if (loading) {
    body = <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement...</span></div>;
  } else if (loadError) {
    body = (
      <div className={styles.frameNotice}>
        <AlertTriangle size={16} />
        <span>Grades{onGameGroup(group)} illisibles : {loadError.replace(/[.\s]+$/, '')}.</span>
        <button type="button" className={styles.secondaryBtn} onClick={retry}><RefreshCw size={14} /> Réessayer</button>
      </div>
    );
  } else {
    body = (
      <div className={styles.frameBody}>
        {/* Liste des grades */}
        <aside className={styles.list}>
          <div className={styles.listHeader}>
            <Award size={18} strokeWidth={2} />
            <span>Grades ({grades.length})</span>
            {canEdit && (
              <button
                className={styles.createBtn}
                title={creating ? 'Annuler' : `Créer un grade${onGameGroup(group)}`}
                onClick={() => { setCreating((v) => !v); setCreateError(null); }}
              >
                {creating ? <X size={16} /> : <Plus size={16} />}
              </button>
            )}
          </div>

          {creating && canEdit && (
            <div className={styles.createForm}>
              <input
                className={styles.input}
                placeholder="Nom du grade (ex: modo)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                autoFocus
              />
              {otherGroups.length > 0 && (
                <div className={styles.alsoOn}>
                  {otherGroups.map((g) => (
                    <label key={g.key} className={styles.alsoOnRow}>
                      <input type="checkbox" checked={alsoOn.includes(g.key)} disabled={createBusy}
                        onChange={() => toggleAlsoOn(g.key)} />
                      Créer aussi sur {g.label}
                    </label>
                  ))}
                </div>
              )}
              <button className={styles.primaryBtn} onClick={handleCreate} disabled={!newName.trim() || createBusy}>
                {createBusy && <Loader2 size={14} className={styles.spin} />}
                Créer{onGameGroup(group)}{extraGroups.length > 0 ? ` et ${extraGroups.map((g) => g.label).join(', ')}` : ''}
              </button>
              {createError && <span className={styles.error}>{createError}</span>}
            </div>
          )}

          <div className={styles.listScroll}>
            {grades.length === 0 && <p className={styles.muted}>Aucun grade.</p>}
            {grades.map((g) => (
              <button
                key={g.name}
                className={`${styles.row} ${selected === g.name ? styles.rowActive : ''}`}
                onClick={() => setSelected(g.name)}
              >
                <Award size={16} className={styles.rowIcon} />
                <span className={styles.rowName}>{g.name}</span>
                {g.weight != null && (
                  <span className={styles.rowWeight} title={`Poids LuckPerms : ${g.weight}`}>{g.weight}</span>
                )}
                {protectedGrades.includes(g.name.toLowerCase()) && (
                  <span className={styles.rowProtected} title="Grade protégé : édition réservée aux admins">
                    <ShieldCheck size={13} aria-label="grade protégé" />
                  </span>
                )}
              </button>
            ))}
          </div>
        </aside>

        {/* Détail : permissions et héritages du grade sélectionné */}
        <section className={`${styles.detail} ${styles.frameDetail}`}>
          {selected ? (
            <GradeDetail
              key={selected}
              name={selected}
              group={group}
              options={opts}
              allGroups={grades}
              protectedGrades={protectedGrades}
              canEdit={canEdit}
              canCosmetics={canCosmetics}
              canMembers={canMembers}
              tab={detailTabOf(detailTab, canCosmetics, canMembers)}
              onTabChange={setDetailTab}
              onWeight={updateWeight}
              onDelete={() => handleDelete(selected)}
            />
          ) : (
            <div className={styles.detailEmpty}>
              <Award size={40} strokeWidth={1.5} />
              <p>
                {canCosmetics
                  ? 'Sélectionne un grade pour gérer ses permissions, ses héritages et ses cosmétiques.'
                  : 'Sélectionne un grade pour gérer ses permissions et ses héritages.'}
              </p>
            </div>
          )}
        </section>
      </div>
    );
  }

  // Jeu sans groupes : le cadre se fond dans la page, comme avant.
  if (!group) return <div className={styles.frame}>{body}</div>;

  return (
    <section
      className={`${styles.frame} ${styles.frameGrouped}`}
      style={group.color ? { '--frame-color': group.color } as CSSProperties : undefined}
      aria-label={`LuckPerms ${group.label}`}
    >
      <div className={styles.frameHeader}>
        <span className={styles.frameCaption}>LuckPerms</span>
        <GroupBadge group={group} size="md" title={`Groupe de jeu : ${group.label}`} />
        {frame.unhealthy && (
          <span className={styles.frameDown} title={`${frame.unhealthy} Le cadre reste utilisable : ses requêtes diront s'il répond.`}>
            <WifiOff size={14} aria-label="injoignable" />
          </span>
        )}
        {!canEdit && (
          <span className={styles.frameReadOnly} title="Permissions et héritages LuckPerms en lecture seule">
            <Eye size={13} /> lecture seule
          </span>
        )}
        {/* Copie vers un autre groupe : droit d'écrire les grades, et au moins un autre groupe de
            l'affectation que la passerelle accepte (les écritures visent ce groupe). */}
        {canEdit && !unavailable && otherGroups.length > 0 && (
          <button
            type="button"
            className={styles.copyGradesBtn}
            onClick={() => setCopying(true)}
            title="Copier la définition des grades de ce groupe vers un autre groupe du projet"
          >
            <Copy size={14} /> Copier les grades vers…
          </button>
        )}
      </div>
      {body}
      {copying && <GradeCopyDialog source={group} targets={otherGroups} onClose={() => setCopying(false)} />}
    </section>
  );
}

interface GradeDetailProps {
  name: string;
  /** Groupe du cadre, nommé dans les messages ; null = jeu sans groupes. */
  group: GameGroupLabel | null;
  /** Requêtes à groupe imposé du cadre. */
  options: RequestOptions | undefined;
  allGroups: LuckPermsGroupDTO[];
  protectedGrades: string[];
  canEdit: boolean;
  /** Onglets Cosmétiques et Boutique (droit config.grades, module cosmetics). */
  canCosmetics: boolean;
  /** Onglet Joueurs (page Joueurs). */
  canMembers: boolean;
  tab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
  /** Poids lu dans les nodes du grade, pour retrier la liste. */
  onWeight: (name: string, weight: number | null) => void;
  onDelete: () => void;
}

function GradeDetail({
  name, group, options, allGroups, protectedGrades, canEdit, canCosmetics, canMembers, tab, onTabChange, onWeight, onDelete,
}: GradeDetailProps) {
  const isAdmin = useAuthStore((s) => !!s.user?.isAdmin);
  const [nodes, setNodes] = useState<GroupNodeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [newPerm, setNewPerm] = useState('');
  const [newValue, setNewValue] = useState(true);
  const [newInherit, setNewInherit] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Grade protégé (whitelist admin, et `admin` toujours) → lecture seule, SAUF pour un admin panel.
  const isProtected = isProtectedGrade(name, protectedGrades) && !isAdmin;
  const readOnly = isProtected || !canEdit;
  // Node réservé aux admins (héritage d'un grade protégé, administration LuckPerms) : motif en clair
  // avant l'envoi. La passerelle refuse de toute façon.
  const refuseNode = (node: string) => (isAdmin ? null : adminOnlyNodeReason(node, protectedGrades));

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getGroupPermissions(name, options);
    setNodes(res.data?.permissions ?? []);
    setLoadError(res.error ?? null);
    setLoading(false);
    if (res.data) onWeight(name, weightFromNodes(res.data.permissions));
  }, [name, options, onWeight]);

  useEffect(() => {
    void load();
  }, [load]);

  const addPerm = async () => {
    const perm = newPerm.trim();
    if (!perm) return;
    const refused = refuseNode(perm);
    if (refused) {
      setError(refused);
      return;
    }
    setBusy(true);
    setError(null);
    const res = await apiService.addGroupPermission(name, perm, newValue, options);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setNewPerm('');
    setNewValue(true);
    await load();
  };

  const removePerm = async (node: GroupNodeDTO) => {
    setError(null);
    const res = await apiService.removeGroupPermission(name, node.key, node.value, options);
    if (res.error) {
      setError(res.error);
      return;
    }
    await load();
  };

  const addInherit = async () => {
    if (!newInherit) return;
    const refused = refuseNode(`group.${newInherit}`);
    if (refused) {
      setError(refused);
      return;
    }
    setBusy(true);
    setError(null);
    const res = await apiService.addGroupPermission(name, `group.${newInherit}`, true, options);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    setNewInherit('');
    await load();
  };

  // Sépare héritages (group.*) et vraies permissions
  const inheritances = nodes.filter((n) => n.key.startsWith('group.'));
  const permissions = nodes.filter((n) => !n.key.startsWith('group.'));

  return (
    <div className={styles.detailInner}>
      <div className={styles.detailHeader}>
        <div className={styles.detailIdentity}>
          <Award size={22} className={styles.detailIcon} />
          <span className={styles.detailName}>{name}</span>
        </div>
        <div className={styles.detailActions}>
          {!readOnly && name !== 'default' && (
            <button className={styles.deleteBtn} onClick={onDelete} title={`Supprimer le grade${onGameGroup(group)}`}>
              <Trash2 size={16} /> Supprimer le grade
            </button>
          )}
        </div>
      </div>

      {(canCosmetics || canMembers) && (
        <div className={styles.tabBar} role="tablist" aria-label={`Réglages du grade ${name}`}>
          <button type="button" role="tab" aria-selected={tab === 'permissions'}
            className={`${styles.tab} ${tab === 'permissions' ? styles.tabActive : ''}`} onClick={() => onTabChange('permissions')}>
            <ShieldCheck size={14} /> Permissions
          </button>
          {canMembers && (
            <button type="button" role="tab" aria-selected={tab === 'joueurs'}
              className={`${styles.tab} ${tab === 'joueurs' ? styles.tabActive : ''}`} onClick={() => onTabChange('joueurs')}>
              <Users size={14} /> Joueurs
            </button>
          )}
          {canCosmetics && (
            <>
              <button type="button" role="tab" aria-selected={tab === 'cosmetiques'}
                className={`${styles.tab} ${tab === 'cosmetiques' ? styles.tabActive : ''}`} onClick={() => onTabChange('cosmetiques')}>
                <Sparkles size={14} /> Cosmétiques
              </button>
              <button type="button" role="tab" aria-selected={tab === 'boutique'}
                className={`${styles.tab} ${tab === 'boutique' ? styles.tabActive : ''}`} onClick={() => onTabChange('boutique')}>
                <Store size={14} /> Boutique
              </button>
            </>
          )}
        </div>
      )}

      {/* Grade protégé : vaut pour tous les onglets (la passerelle et l'API des packs refusent aussi). */}
      {isProtected && (
        <div className={styles.protectedNotice}>
          <ShieldCheck size={16} /> Le grade <strong>{name}</strong> est protégé : édition réservée aux admins (lecture seule).
        </div>
      )}

      {/* Cosmétiques et boutique : réglages des containers, communs à tous les groupes. En lecture
          seule pour un grade protégé seulement : le droit LuckPerms (canEdit) ne les concerne pas. */}
      {(tab === 'cosmetiques' || tab === 'boutique') && <GradeSettingsPanel grade={name} tab={tab} readOnly={isProtected} />}

      {/* Joueurs du grade : LuckPerms du groupe du cadre, sans base de joueurs (lobby compris). */}
      {tab === 'joueurs' && <GradeMembers name={name} group={group} options={options} protectedGrades={protectedGrades} />}

      {tab === 'permissions' && !isProtected && canEdit && (
        <>
          {/* Ajout de permission */}
          <div className={styles.addForm}>
            <input
              className={styles.input}
              placeholder="Permission (ex: recube.fly, essentials.tp...)"
              value={newPerm}
              onChange={(e) => setNewPerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addPerm()}
            />
            <button
              type="button"
              className={`${styles.toggleBtn} ${newValue ? styles.toggleTrue : styles.toggleFalse}`}
              onClick={() => setNewValue((v) => !v)}
              title="Autoriser (true) / Interdire (false)"
            >
              {newValue ? 'true' : 'false'}
            </button>
            <button className={styles.primaryBtn} onClick={addPerm} disabled={busy || !newPerm.trim()}>
              <Plus size={16} /> Ajouter
            </button>
          </div>

          {/* Ajout d'héritage */}
          <div className={styles.addForm}>
            <select
              className={styles.input}
              value={newInherit}
              onChange={(e) => setNewInherit(e.target.value)}
            >
              <option value="">Hériter d'un grade…</option>
              {allGroups
                .filter((g) => g.name.toLowerCase() !== name.toLowerCase())
                // Hériter d'un grade protégé reviendrait à prendre ses droits : admins seulement.
                .filter((g) => isAdmin || !isProtectedGrade(g.name, protectedGrades))
                .map((g) => (
                  <option key={g.name} value={g.name}>{g.name}</option>
                ))}
            </select>
            <button className={styles.primaryBtn} onClick={addInherit} disabled={busy || !newInherit}>
              <Plus size={16} /> Héritage
            </button>
          </div>
        </>
      )}

      {tab === 'permissions' && error && <span className={styles.error}>{error}</span>}

      {tab !== 'permissions' ? null : loading ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement...</span></div>
      ) : loadError ? (
        <span className={styles.error}>Permissions illisibles : {loadError}</span>
      ) : (
        <>
          <div className={styles.sectionLabel}><ShieldCheck size={14} /> Permissions ({permissions.length})</div>
          {permissions.length === 0 ? (
            <p className={styles.muted}>Aucune permission spécifique.</p>
          ) : (
            <ul className={styles.permList}>
              {permissions.map((n) => (
                <li key={`${n.key}:${n.value}`} className={styles.permRow}>
                  <span className={styles.permKey}>{n.key}</span>
                  <span className={`${styles.permValue} ${n.value ? styles.vTrue : styles.vFalse}`}>
                    {n.value ? <Check size={12} /> : <X size={12} />}
                    {n.value ? 'true' : 'false'}
                  </span>
                  {!readOnly && (
                    <button className={styles.removeBtn} onClick={() => removePerm(n)} title="Retirer">
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {inheritances.length > 0 && (
            <>
              <div className={styles.sectionLabel}><Award size={14} /> Héritages</div>
              <ul className={styles.permList}>
                {inheritances.map((n) => (
                  <li key={n.key} className={styles.permRow}>
                    <span className={styles.permKey}>{n.key.replace('group.', '')}</span>
                    {!readOnly && (
                      <button className={styles.removeBtn} onClick={() => removePerm(n)} title="Retirer l'héritage">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
